import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { Download, Star, Archive, ArrowClockwise, Warning, CaretDown, CaretRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { humanizeAxiosError } from "@/lib/api";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PayrollAdmin() {
  const { user } = useAuth();
  const canRecall = ["hr", "admin"].includes(user?.role);
  const [data, setData] = useState({ signed: [], rejected: [], pending: [] });
  // Aylık gruplama için hangi (tab, ay) başlıklarının açık olduğunu izler
  const [openMonths, setOpenMonths] = useState(() => new Set());
  const toggleMonth = (key) => setOpenMonths((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const monthLabel = (period) => {
    // period is "YYYY-MM"
    if (!period || !/^\d{4}-\d{2}$/.test(period)) return period || "?";
    const [y, m] = period.split("-").map(Number);
    const names = ["Ocak","Şubat","Mart","Nisan","Mayıs","Haziran","Temmuz","Ağustos","Eylül","Ekim","Kasım","Aralık"];
    return `${names[m - 1]} ${y}`;
  };
  const groupByMonth = (arr) => {
    const byMonth = new Map();
    for (const p of arr || []) {
      const key = p.period || "?";
      if (!byMonth.has(key)) byMonth.set(key, []);
      byMonth.get(key).push(p);
    }
    // Sort months desc (newest first)
    return [...byMonth.entries()].sort((a, b) => (a[0] < b[0] ? 1 : -1));
  };
  const [feedback, setFeedback] = useState({ count: 0, average: 0, distribution: {1:0,2:0,3:0,4:0,5:0}, entries: [] });
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [recalled, setRecalled] = useState([]);
  const [recallOpen, setRecallOpen] = useState(null); // payroll to recall
  const [recallReason, setRecallReason] = useState("");
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkForm, setBulkForm] = useState({ month: "", year: String(new Date().getFullYear()), reason: "", only_unsigned: false });

  const reload = () => {
    api.get("/payroll/admin-summary").then((r) => setData(r.data)).catch(() => {});
    api.get("/payroll/feedback-summary").then((r) => setFeedback(r.data)).catch(() => {});
    if (canRecall) api.get("/payroll/recalled").then((r) => setRecalled(r.data)).catch(() => {});
  };

  const exportNonApprovers = async () => {
    try {
      const r = await api.get("/payroll/non-approvers-export", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `bordro_onaylamayanlar_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Onaylamayanlar listesi indirildi");
    } catch (e) { toast.error(humanizeAxiosError(e)); }
  };

  const runHealthCheck = async () => {
    setHealthLoading(true);
    try {
      const r = await api.get("/payroll/health-check");
      setHealth(r.data);
      const total = (r.data.missing?.length || 0) + (r.data.invalid?.length || 0);
      if (total === 0) toast.success("Tüm bordro PDF'leri sağlam.");
      else toast.warning(`${total} sorunlu dosya bulundu`);
    } catch (e) { toast.error(humanizeAxiosError(e)); }
    finally { setHealthLoading(false); }
  };

  const purgePayroll = async (pid) => {
    if (!window.confirm("Bu bozuk bordro kaydını silmek istediğinizden emin misiniz? Personelin listesinden de kaldırılacak.")) return;
    try {
      await api.post(`/payroll/${pid}/purge`);
      toast.success("Kayıt silindi. Bordroyu tekrar yükleyebilirsiniz.");
      await runHealthCheck();
      api.get("/payroll/admin-summary").then((r) => setData(r.data));
    } catch (e) { toast.error(humanizeAxiosError(e)); }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canRecall]);

  const submitRecall = async () => {
    if (!recallOpen) return;
    if (!recallReason || recallReason.length < 3) return toast.error("Sebep en az 3 karakter olmalı");
    try {
      await api.post(`/payroll/${recallOpen.id}/recall`, { reason: recallReason });
      toast.success("Bordro geri çekildi ve personele bildirim gönderildi");
      setRecallOpen(null); setRecallReason("");
      reload();
    } catch (e) { toast.error(humanizeAxiosError(e)); }
  };

  const submitBulkRecall = async () => {
    if (!bulkForm.reason || bulkForm.reason.length < 3) return toast.error("Sebep en az 3 karakter olmalı");
    if (!bulkForm.month && !bulkForm.year) return toast.error("En az bir filtre (ay veya yıl) belirtin");
    if (!window.confirm(`${bulkForm.month || "tüm aylar"} / ${bulkForm.year} için toplu geri çekmeyi onaylıyor musunuz?`)) return;
    try {
      const body = { reason: bulkForm.reason, only_unsigned: bulkForm.only_unsigned };
      if (bulkForm.month) body.month = Number(bulkForm.month);
      if (bulkForm.year) body.year = Number(bulkForm.year);
      const r = await api.post("/payroll/recall-bulk", body);
      toast.success(`${r.data.recalled_count} bordro geri çekildi`);
      setBulkOpen(false);
      setBulkForm({ month: "", year: String(new Date().getFullYear()), reason: "", only_unsigned: false });
      reload();
    } catch (e) { toast.error(humanizeAxiosError(e)); }
  };

  const filesForRow = (p) => {
    // Prefer the signed (stamped) version when available.
    return p.signed_file_url || p.file_url || `/api/uploads/payroll/${p.id}`;
  };

  const downloadAllForUser = async (userId, userName) => {
    try {
      toast.loading("Bordrolar hazırlanıyor…", { id: "zip-" + userId });
      const r = await api.get(`/payroll/user/${userId}/download-all`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `bordrolar_${(userName || "personel").replace(/\s+/g, "_")}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Zip indirildi", { id: "zip-" + userId });
    } catch (e) {
      toast.error(humanizeAxiosError(e), { id: "zip-" + userId });
    }
  };

  const applyStamp = async (p) => {
    if (!window.confirm(`${p.user_name} adlı personelin ${p.period} bordrosuna şirket kaşesi uygulansın mı?`)) return;
    try {
      const r = await api.post(`/payroll/${p.id}/apply-stamp`);
      toast.success("Kaşe uygulandı");
      window.open(`${API}${r.data.stamped_file_url}`, "_blank");
      // Reload summary so button reflects updated state.
      api.get("/payroll/admin-summary").then((r2) => setData(r2.data));
    } catch (e) {
      toast.error(humanizeAxiosError(e));
    }
  };

  // Gönderilme tarihi (uploaded_at) — YYYY-MM-DD ISO stringlerle
  // sıralaması alfabetik sırayla aynıdır; en yeni gönderim en üstte olacak
  // biçimde azalan sıralama uygularız. Backend zaten sıralı döner ama frontend
  // katmanında da garanti altına alıyoruz.
  const byUploadedDesc = React.useCallback((a, b) => {
    const av = a.uploaded_at || "";
    const bv = b.uploaded_at || "";
    if (av === bv) return 0;
    return av < bv ? 1 : -1;
  }, []);

  const sortedData = React.useMemo(() => ({
    signed: [...(data.signed || [])].sort(byUploadedDesc),
    pending: [...(data.pending || [])].sort(byUploadedDesc),
    rejected: [...(data.rejected || [])].sort(byUploadedDesc),
  }), [data, byUploadedDesc]);

  // Group by period YYYY-MM for monthly tab.
  const groupedByMonth = React.useMemo(() => {
    const all = [...(sortedData.signed || []), ...(sortedData.pending || []), ...(sortedData.rejected || [])];
    const map = new Map();
    for (const p of all) {
      const k = (p.period || "").slice(0, 7) || "-";
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(p);
    }
    // Ay içindeki kayıtları da yüklenme tarihine göre sırala.
    for (const [, arr] of map.entries()) arr.sort(byUploadedDesc);
    // En yeni yüklenmesi olan ay üstte olacak şekilde sırala.
    const arr = Array.from(map.entries()).sort((a, b) => {
      const la = a[1][0]?.uploaded_at || a[0];
      const lb = b[1][0]?.uploaded_at || b[0];
      return la < lb ? 1 : -1;
    });
    return arr;
  }, [sortedData, byUploadedDesc]);

  const maxDist = Math.max(1, ...Object.values(feedback.distribution || {}));

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Bordro</div>
          <h1 className="font-display text-3xl font-bold mt-1">Bordro Onay Takibi</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="default" size="sm" onClick={exportNonApprovers} data-testid="export-non-approvers">
            <Download size={14} className="mr-1" /> Onaylamayanları İndir (Excel)
          </Button>
          {canRecall && (
            <Button variant="outline" size="sm" onClick={() => setBulkOpen(true)} data-testid="bulk-recall-btn" className="text-destructive">
              <ArrowClockwise size={14} className="mr-1" /> Toplu Geri Çek
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={runHealthCheck} disabled={healthLoading} data-testid="payroll-health-check">
            {healthLoading ? "Kontrol ediliyor…" : "PDF Bütünlük Kontrolü"}
          </Button>
        </div>
      </div>

      {health && (health.missing?.length > 0 || health.invalid?.length > 0) && (
        <Card className="border-destructive">
          <CardContent className="p-4 space-y-3">
            <div className="text-sm font-semibold text-destructive">
              {(health.missing?.length || 0) + (health.invalid?.length || 0)} sorunlu bordro bulundu
              <span className="text-xs text-muted-foreground ml-2">(sağlam: {health.total_ok || 0})</span>
            </div>
            {[...(health.missing || []).map((e) => ({...e, kind: "Dosya bulunamıyor"})),
              ...(health.invalid || []).map((e) => ({...e, kind: `Geçersiz PDF (${e.size||0}B)`}))].map((e, i) => (
              <div key={i} className="flex items-center justify-between gap-2 rounded-md border p-2 text-sm flex-wrap">
                <div>
                  <span className="font-semibold">{e.user_name}</span>
                  <span className="text-muted-foreground ml-2">{e.period} · {e.field}</span>
                  <div className="text-xs text-destructive">{e.kind}</div>
                </div>
                <Button size="sm" variant="destructive" onClick={() => purgePayroll(e.payroll_id)} data-testid={`purge-${e.payroll_id}`}>Kaydı Sil</Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
      <Tabs defaultValue="signed">
        <TabsList>
          <TabsTrigger value="signed" data-testid="tab-signed">Onaylayan ({sortedData.signed.length})</TabsTrigger>
          <TabsTrigger value="pending" data-testid="tab-pending-app">Onaylamayan / Bekleyen ({sortedData.pending.length})</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="tab-rejected">Reddeden ({sortedData.rejected.length})</TabsTrigger>
          <TabsTrigger value="monthly" data-testid="tab-monthly">Aylık Bakış ({groupedByMonth.length})</TabsTrigger>
          <TabsTrigger value="feedback" data-testid="tab-feedback">Memnuniyet ({feedback.count})</TabsTrigger>
          {canRecall && <TabsTrigger value="recalled" data-testid="tab-recalled">Geri Çekilen ({recalled.length})</TabsTrigger>}
        </TabsList>

        {["signed", "pending", "rejected"].map((k) => {
          const monthlyGroups = groupByMonth(sortedData[k]);
          return (
          <TabsContent value={k} key={k}>
            {monthlyGroups.length === 0 && (
              <Card><CardContent className="p-6 text-center text-muted-foreground">Kayıt yok</CardContent></Card>
            )}
            <div className="space-y-2">
              {monthlyGroups.map(([period, rows]) => {
                const mkey = `${k}:${period}`;
                const open = openMonths.has(mkey);
                return (
                  <Card key={mkey}>
                    <button
                      type="button"
                      className="w-full flex items-center justify-between gap-3 p-3 text-left hover:bg-muted/40 rounded-t-lg transition-colors"
                      onClick={() => toggleMonth(mkey)}
                      aria-expanded={open}
                      data-testid={`month-toggle-${k}-${period}`}
                    >
                      <div className="flex items-center gap-2">
                        {open ? <CaretDown size={16} /> : <CaretRight size={16} />}
                        <span className="font-display font-semibold">{monthLabel(period)}</span>
                        <span className="text-xs text-muted-foreground">({rows.length} personel)</span>
                      </div>
                      <Badge variant={k === "signed" ? "default" : k === "rejected" ? "destructive" : "secondary"}>
                        {k === "signed" ? "İmzalı" : k === "rejected" ? "Reddedildi" : "Bekleyen"}
                      </Badge>
                    </button>
                    {open && (
                      <CardContent className="p-0 border-t border-border">
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                              <tr>
                                <th className="text-left p-3">Personel</th>
                                <th className="text-left p-3">
                                  <span className="inline-flex items-center gap-1">Yüklenme <span className="text-primary" title="En yeni önce">▼</span></span>
                                </th>
                                <th className="text-left p-3">Son Tarih</th>
                                <th className="text-left p-3">Durum</th>
                                <th className="text-left p-3">Detay</th>
                              </tr>
                            </thead>
                            <tbody>
                              {rows.map((p) => (
                                <tr key={p.id} className="border-t border-border" data-testid={`row-${k}-${p.id}`}>
                                  <td className="p-3">{p.user_name}</td>
                                  <td className="p-3">{p.uploaded_at?.slice(0, 10)}</td>
                                  <td className="p-3">{p.deadline?.slice(0, 10)}</td>
                                  <td className="p-3">
                                    {k === "signed" && <Badge>İmzalandı · {p.signed_at?.slice(0, 10)}</Badge>}
                                    {k === "pending" && <Badge variant="secondary">Beklemede</Badge>}
                                    {k === "rejected" && <Badge variant="destructive">Reddedildi · {p.rejected_at?.slice(0, 10)}</Badge>}
                                  </td>
                                  <td className="p-3 text-xs">
                                    {p.reject_reason && <div className="text-destructive">Ret: {p.reject_reason}</div>}
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <a href={`${API}${filesForRow(p)}`} target="_blank" rel="noreferrer" className="text-primary underline inline-flex items-center gap-1"><Download size={12} />PDF</a>
                                      {p.stamped_file_url && (
                                        <a href={`${API}${p.stamped_file_url}`} target="_blank" rel="noreferrer" className="text-primary underline inline-flex items-center gap-1" title="Kaşeli sürüm">
                                          Kaşeli
                                        </a>
                                      )}
                                      <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px]" onClick={() => downloadAllForUser(p.user_id, p.user_name)} data-testid={`zip-${p.user_id}`} title="Bu personelin tüm bordrolarını ZIP indir">
                                        <Archive size={12} className="mr-1" />ZIP
                                      </Button>
                                      {k === "signed" && (
                                        <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px]" onClick={() => applyStamp(p)} data-testid={`stamp-${p.id}`} title="Bu bordroya şirket kaşesi uygula">
                                          Kaşe Bas
                                        </Button>
                                      )}
                                      {canRecall && (
                                        <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px] text-destructive" onClick={() => { setRecallOpen(p); setRecallReason(""); }} data-testid={`recall-${p.id}`} title="Bordroyu geri çek">
                                          <ArrowClockwise size={12} className="mr-1" />Geri Çek
                                        </Button>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </CardContent>
                    )}
                  </Card>
                );
              })}
            </div>
          </TabsContent>
          );
        })}

        <TabsContent value="monthly">
          <div className="space-y-3">
            {groupedByMonth.map(([month, entries]) => {
              const signedC = entries.filter((e) => e.signed_at).length;
              const rejectedC = entries.filter((e) => e.rejected_at).length;
              const pendingC = entries.length - signedC - rejectedC;
              return (
                <Card key={month}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
                      <div className="font-display font-bold text-lg">{month || "Belirtilmemiş"}</div>
                      <div className="flex gap-2 text-xs">
                        <Badge>{signedC} imzalı</Badge>
                        <Badge variant="secondary">{pendingC} bekleyen</Badge>
                        <Badge variant="destructive">{rejectedC} red</Badge>
                      </div>
                    </div>
                    <div className="text-sm text-muted-foreground">Toplam {entries.length} bordro</div>
                  </CardContent>
                </Card>
              );
            })}
            {groupedByMonth.length === 0 && <div className="text-sm text-muted-foreground text-center p-6">Kayıt yok</div>}
          </div>
        </TabsContent>

        <TabsContent value="feedback">
          <div className="grid md:grid-cols-3 gap-4">
            <Card className="md:col-span-1">
              <CardContent className="p-6 space-y-4">
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Ortalama Puan</div>
                <div className="flex items-baseline gap-2">
                  <div className="font-display text-5xl font-bold">{feedback.average || 0}</div>
                  <div className="text-muted-foreground">/ 5</div>
                </div>
                <div className="flex gap-1 text-warning">
                  {[1,2,3,4,5].map((n) => (
                    <Star key={n} size={20} weight={n <= Math.round(feedback.average) ? "fill" : "regular"} />
                  ))}
                </div>
                <div className="text-sm text-muted-foreground">Toplam {feedback.count} personel geri bildirim verdi.</div>
                <div className="space-y-2 pt-2">
                  {[5,4,3,2,1].map((n) => {
                    const c = feedback.distribution?.[n] || 0;
                    const pct = Math.round((c / maxDist) * 100);
                    return (
                      <div key={n} className="flex items-center gap-2 text-xs">
                        <div className="w-6 text-right">{n}★</div>
                        <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                          <div className="h-full bg-warning" style={{ width: `${pct}%` }} />
                        </div>
                        <div className="w-8 text-right text-muted-foreground">{c}</div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
            <Card className="md:col-span-2">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                      <tr>
                        <th className="text-left p-3">Personel</th>
                        <th className="text-left p-3">Dönem</th>
                        <th className="text-left p-3">Puan</th>
                        <th className="text-left p-3">Yorum</th>
                        <th className="text-left p-3">Tarih</th>
                      </tr>
                    </thead>
                    <tbody>
                      {feedback.entries.map((e) => (
                        <tr key={e.payroll_id} className="border-t border-border">
                          <td className="p-3">{e.user_name}</td>
                          <td className="p-3">{e.period}</td>
                          <td className="p-3">
                            <div className="flex gap-0.5 text-warning">
                              {[1,2,3,4,5].map((n) => <Star key={n} size={14} weight={n <= (e.rating || 0) ? "fill" : "regular"} />)}
                            </div>
                          </td>
                          <td className="p-3 max-w-md">
                            {e.comment ? <span className="text-muted-foreground">{e.comment}</span> : <span className="text-muted-foreground/50 italic">yorum yok</span>}
                          </td>
                          <td className="p-3 text-xs text-muted-foreground">{e.at?.slice(0, 10)}</td>
                        </tr>
                      ))}
                      {feedback.entries.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-muted-foreground">Henüz geri bildirim yok</td></tr>}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {canRecall && (
          <TabsContent value="recalled">
            <Card><CardContent className="p-6 space-y-3">
              <div className="text-xs text-muted-foreground rounded-md border border-warning/30 bg-warning/10 p-2 flex items-start gap-2">
                <Warning size={14} className="mt-0.5 text-warning" />
                <span>Yanlış gönderilen bordrolar burada listelenir. Personel geri çekilen bordroyu göremez ve imzalayamaz. Doğrusu yeniden yüklenebilir.</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                    <tr>
                      <th className="text-left p-3">Personel</th>
                      <th className="text-left p-3">Dönem</th>
                      <th className="text-left p-3">Geri Çekme Tarihi</th>
                      <th className="text-left p-3">Geri Çeken</th>
                      <th className="text-left p-3">Sebep</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recalled.map((p) => (
                      <tr key={p.id} className="border-t border-border">
                        <td className="p-3">{p.user_name}</td>
                        <td className="p-3">{p.period}</td>
                        <td className="p-3 text-xs">{p.recalled_at?.slice(0, 19).replace("T", " ")}</td>
                        <td className="p-3 text-xs">{p.recalled_by_name}</td>
                        <td className="p-3 text-xs max-w-md">{p.recall_reason}</td>
                      </tr>
                    ))}
                    {recalled.length === 0 && (
                      <tr><td colSpan={5} className="p-6 text-center text-muted-foreground">Henüz geri çekilmiş bordro yok.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent></Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Individual recall dialog */}
      <Dialog open={!!recallOpen} onOpenChange={(o) => !o && setRecallOpen(null)}>
        <DialogContent data-testid="recall-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ArrowClockwise size={20} />Bordroyu Geri Çek</DialogTitle>
            <DialogDescription>
              {recallOpen && (
                <>
                  <span className="font-medium">{recallOpen.user_name}</span> · {recallOpen.period}
                  <br />Personel bu bordroyu artık göremeyecek ve imzalayamayacak. İşlem geri alınamaz — yerine doğru bordroyu tekrar yükleyebilirsiniz.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Geri Çekme Sebebi <span className="text-destructive">*</span></Label>
              <Textarea rows={3} value={recallReason} onChange={(e) => setRecallReason(e.target.value)} placeholder="Örn: Yanlış personele yüklendi, tutarlar hatalı..." data-testid="recall-reason" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setRecallOpen(null)}>Vazgeç</Button>
              <Button variant="destructive" onClick={submitRecall} data-testid="recall-submit">Geri Çek</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk recall dialog */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent data-testid="bulk-recall-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ArrowClockwise size={20} />Toplu Bordro Geri Çekme</DialogTitle>
            <DialogDescription>Seçilen ay/yıl'a ait tüm bordrolar geri çekilir. Personellere otomatik bildirim gider.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Yıl <span className="text-destructive">*</span></Label>
                <Input type="number" value={bulkForm.year} onChange={(e) => setBulkForm({...bulkForm, year: e.target.value})} data-testid="bulk-year" />
              </div>
              <div>
                <Label>Ay (opsiyonel)</Label>
                <Select value={bulkForm.month || "all"} onValueChange={(v) => setBulkForm({...bulkForm, month: v === "all" ? "" : v})}>
                  <SelectTrigger data-testid="bulk-month"><SelectValue placeholder="Tüm aylar" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tüm aylar</SelectItem>
                    {["1","2","3","4","5","6","7","8","9","10","11","12"].map((m) => (
                      <SelectItem key={m} value={m}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Sebep <span className="text-destructive">*</span></Label>
              <Textarea rows={3} value={bulkForm.reason} onChange={(e) => setBulkForm({...bulkForm, reason: e.target.value})} placeholder="Örn: Bordro hesaplama hatası tespit edildi, yeniden hesaplanacak..." data-testid="bulk-reason" />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={bulkForm.only_unsigned} onCheckedChange={(v) => setBulkForm({...bulkForm, only_unsigned: !!v})} data-testid="bulk-unsigned" />
              <span>Sadece imzalanmamış olanları geri çek</span>
            </label>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setBulkOpen(false)}>Vazgeç</Button>
              <Button variant="destructive" onClick={submitBulkRecall} data-testid="bulk-recall-submit">Toplu Geri Çek</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
